<template>
  <div class="min-h-screen flex items-center justify-center px-4">
    <div class="w-full max-w-4xl">
      <!-- Hero Section -->
      <div class="text-center mb-12">
        <h1
          class="text-5xl md:text-6xl font-bold mb-4 pb-1 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"
          data-test="search-title">
          {{ t('search.title') }}
        </h1>
        <p class="text-xl text-gray-600 max-w-2xl mx-auto">
          {{ t('search.subtitle') }}
        </p>
      </div>

      <!-- Search Card -->
      <div class="search-card max-w-5xl mx-auto">
        <form class="space-y-6" @submit.prevent="searchFlights">
          <!-- Search Fields Row -->
          <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
            <!-- From -->
            <div class="input-group">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ t('search.from') }}
              </label>
              <select
                v-model="searchForm.origin"
                required
                name="origin"
                class="input-field"
                data-test="origin-select"
              >
                <option value="">{{ t('search.fromPlaceholder') }}</option>
                <option v-for="city in cities" :key="city.code" :value="city.code">
                  {{ t(`dynamic.cities.${city.translationKey}`) }}
                </option>
              </select>
            </div>

            <!-- To -->
            <div class="input-group">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ t('search.to') }}
              </label>
              <select
                v-model="searchForm.destination"
                name="destination"
                required
                class="input-field"
                data-test="destination-select"
              >
                <option value="">{{ t('search.toPlaceholder') }}</option>
                <option v-for="city in cities" :key="city.code" :value="city.code">
                  {{ t(`dynamic.cities.${city.translationKey}`) }}
                </option>
              </select>
            </div>

            <!-- Date -->
            <div class="input-group">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ t('search.date') }}
              </label>
              <DatePicker
                v-model="searchForm.date"
                :placeholder="t('search.datePlaceholder')"
                :min-date="useDate().todayDate()"
                data-test="date-picker"
              />
            </div>

            <!-- Search Button -->
            <div class="flex items-end">
              <button
                type="submit"
                :disabled="isSearching"
                class="btn-primary w-full flex items-center justify-center"
                data-test="search-button"
              >
                <svg v-if="isSearching" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                {{ isSearching ? t('common.loading') : t('search.searchButton') }}
              </button>
            </div>
          </div>
        </form>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import type { City, SearchForm, SearchPayload } from '~/types'
import { useI18n } from '#i18n'
import { useDate } from '~/composables/useDate'
import { useApi } from '~/composables/useApi'
import DatePicker from '~/components/DatePicker.vue'

const { t } = useI18n()
const { initiateSearch } = useApi()

// Form data with TypeScript typing
const searchForm = ref<SearchForm>({
  origin: 'THR',
  destination: 'MHD',
  date: '2025-06-10'
})

const isSearching = ref<boolean>(false)

// Cities data with TypeScript typing
const cities: City[] = [
  { code: 'THR', translationKey: 'tehran', name: 'Tehran' },
  { code: 'MHD', translationKey: 'mashhad', name: 'Mashhad' },
  { code: 'IFN', translationKey: 'isfahan', name: 'Isfahan' },
  { code: 'SYZ', translationKey: 'shiraz', name: 'Shiraz' },
  { code: 'TBZ', translationKey: 'tabriz', name: 'Tabriz' },
  { code: 'AWZ', translationKey: 'ahvaz', name: 'Ahvaz' },
  { code: 'KER', translationKey: 'kerman', name: 'Kerman' },
  { code: 'KIH', translationKey: 'kish', name: 'Kish' },
  { code: 'OMH', translationKey: 'urmia', name: 'Urmia' },
  { code: 'RAS', translationKey: 'rasht', name: 'Rasht' },
  { code: 'BND', translationKey: 'bandarabbas', name: 'Bandar Abbas' }
]

const searchFlights = async (): Promise<void> => {
  try {
    isSearching.value = true

    // Create search payload matching FastAPI Search model
    const payload: SearchPayload = {
      origin: searchForm.value.origin,
      destination: searchForm.value.destination,
      date: searchForm.value.date,
      adult: 1,
      child: 0,
      infant: 0
    }

    // Send POST request to FastAPI search endpoint using the API composable
    await initiateSearch(payload)


    // Navigate to results page with search parameters
    await navigateTo({
      path: '/results',
      query: {
        origin: searchForm.value.origin,
        destination: searchForm.value.destination,
        date: searchForm.value.date
      }
    })

  } catch (error) {
    console.error('Search error:', error)
    // For now, continue to results page even on error for demo purposes
    await navigateTo({
      path: '/results',
      query: {
        origin: searchForm.value.origin,
        destination: searchForm.value.destination,
        date: searchForm.value.date,
        searchId: Date.now().toString()
      }
    })
  } finally {
    isSearching.value = false
  }
}

// Page metadata
useHead({
  title: () => t('search.title'),
  meta: [
    { name: 'description', content: () => t('search.subtitle') }
  ]
})
</script>
